import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../contexts/AuthProvider';
import { API_URL } from '../../utils/config';
import './AddOrganizerForm.css';

const EditOrganizerForm = () => {
  const { t } = useTranslation('organizers');
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAuthenticated } = useContext(AuthContext);
  
  const [formData, setFormData] = useState({
    firstName: '',
    familyName: '',
    phone: '',
    email: '',
    password: '',
    role: 'viewer'
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [apiError, setApiError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const baseUrl = API_URL;

  const getLangFromLocalStorage = () => {
    return localStorage.getItem('language') || 'en';
  };

  // Load organizer data
  useEffect(() => {
    const fetchOrganizerData = async () => {
      try {
        setIsLoadingData(true);
        const response = await fetch(`${baseUrl}/api/v1/attnd-venue/organizer/${id}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Accept-Language': getLangFromLocalStorage(),
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Fetched organizer data:', data);
        console.log('Data type:', typeof data);
        console.log('Data keys:', Object.keys(data || {}));
        console.log('First name from API (correct):', data?.first_name);
        console.log('First name from API (typo):', data?.fisrt_name);
        console.log('Family name from API:', data?.family_name);
        console.log('Email from API:', data?.email);
        console.log('Role from API:', data?.role);

        // Handle direct response structure - data is the organizer object itself
        if (data && data.id) {
          const newFormData = {
            firstName: data.first_name || data.fisrt_name || '', // Handle API typo
            familyName: data.family_name || '',
            phone: data.phone || '',
            email: data.email || '',
            password: '', // Don't pre-fill password for security
            role: data.role || 'viewer'
          };
          console.log('Setting form data:', newFormData);
          setFormData(newFormData);
        } else {
          console.log('Data validation failed - no ID found or data is null/undefined');
        }
      } catch (error) {
        console.error('Error fetching organizer:', error);
        setApiError('Failed to load organizer data');
      } finally {
        setIsLoadingData(false);
      }
    };

    if (id) {
      fetchOrganizerData();
    }
  }, [id, baseUrl]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear API error when user makes changes
    if (apiError) {
      setApiError('');
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) {
          return t('firstNameRequired');
        }
        if (value.length < 2) {
          return t('firstNameMinLength');
        }
        if (!/^[a-zA-Z\u0600-\u06FF\s]+$/.test(value)) {
          return t('firstNameInvalidChars');
        }
        return '';

      case 'familyName':
        if (!value.trim()) {
          return t('familyNameRequired');
        }
        if (value.length < 2) {
          return t('familyNameMinLength');
        }
        if (!/^[a-zA-Z\u0600-\u06FF\s]+$/.test(value)) {
          return t('familyNameInvalidChars');
        }
        return '';

      case 'phone':
        if (!value.trim()) {
          return t('phoneRequired');
        }
        if (!/^[\+]?[\d\s\-\(\)]+$/.test(value) || value.length < 8) {
          return t('phoneInvalid');
        }
        return '';

      case 'email':
        if (!value.trim()) {
          return t('emailRequired');
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return t('emailInvalid');
        }
        return '';

      case 'password':
        // Password is optional for edit - only validate if provided
        if (value) {
          const errors = [];
          if (value.length < 8) errors.push(t('passwordMinLength'));
          if (!/[a-z]/.test(value)) errors.push(t('passwordLowercase'));
          if (!/[A-Z]/.test(value)) errors.push(t('passwordUppercase'));
          if (!/\d/.test(value)) errors.push(t('passwordNumber'));
          if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(value)) errors.push(t('passwordSpecialChar'));
          
          if (errors.length > 0) {
            return `${t('passwordMustContain')}: ${errors.join(', ')}`;
          }
        }
        return '';

      case 'role':
        if (!value || !['viewer', 'manager'].includes(value)) {
          return t('roleRequired');
        }
        return '';

      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError('');

    try {
      // Create request body - only include password if it's provided
      const requestBody = {
        first_name: formData.firstName,
        family_name: formData.familyName,
        phone: formData.phone,
        email: formData.email,
        role: formData.role
      };

      // Only include password if it's provided
      if (formData.password.trim()) {
        requestBody.password = formData.password;
      }

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/organizer/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': getLangFromLocalStorage(),
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('API Response:', data);

      if (!response.ok) {
        if (data.message && data.message.includes('already exists')) {
          setApiError(t('emailOrUsernameExists'));
        } else {
          setApiError(data.message || `HTTP error! status: ${response.status}`);
        }
        return;
      }

      // Success
      setIsSuccess(true);
      
      setTimeout(() => {
        navigate('/organizers');
      }, 2000);

    } catch (error) {
      console.error('Error updating organizer:', error);
      setApiError(error.message || 'Network error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/organizers');
  };

  if (isLoadingData) {
    return (
      <div className="add-organizer-container">
        <div className="form-container">
          <div className="form-header">
            <h1 className="form-title">{t('loading')}</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <div>Unauthorized</div>;
  }

  return (
    <div className="add-organizer-container">
      {/* Title */}
      <div className="organizers-header">
        <h1 className="organizers-title" onClick={() => navigate('/organizers')} style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'}}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 22.75C6.07 22.75 1.25 17.93 1.25 12C1.25 6.07 6.07 1.25 12 1.25C17.93 1.25 22.75 6.07 22.75 12C22.75 17.93 17.93 22.75 12 22.75ZM12 2.75C6.9 2.75 2.75 6.9 2.75 12C2.75 17.1 6.9 21.25 12 21.25C17.1 21.25 21.25 17.1 21.25 12C21.25 6.9 17.1 2.75 12 2.75Z" fill="#464646"/>
            <path d="M13.2599 16.28C13.0699 16.28 12.8799 16.21 12.7299 16.06L9.19992 12.53C8.90992 12.24 8.90992 11.76 9.19992 11.47L12.7299 7.94C13.0199 7.65 13.4999 7.65 13.7899 7.94C14.0799 8.23 14.0799 8.71 13.7899 9L10.7899 12L13.7899 15C14.0799 15.29 14.0799 15.77 13.7899 16.06C13.6499 16.21 13.4599 16.28 13.2599 16.28Z" fill="#464646"/>
          </svg>
          {t('organizers')}
        </h1>
      </div>

      {/* Edit Organizer Title */}
      <div className="organizers-controls">
        <div className="controls-left">
          <h2 className="all-organizers-title">{t('editOrganizer')}</h2>
        </div>
      </div>

      {/* Success Message */}
      {isSuccess && (
        <div className="success-message">
          <span>{t('organizerUpdatedSuccess')}</span>
        </div>
      )}
      
      {/* API Error */}
      {apiError && (
        <div className="api-error">
          <span>{apiError}</span>
        </div>
      )}

      <div className="form-container">
        <div className="form-header">
        </div>

        <form onSubmit={handleSubmit} className="organizer-form">
          {/* First Name */}
          <div className="form-group">
            <label htmlFor="firstName" className="form-label">
              {t('firstName')} *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder={t('enterFirstName')}
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              disabled={true}
            />
            {errors.firstName && (
              <span className="error-message">{errors.firstName}</span>
            )}
          </div>

          {/* Family Name */}
          <div className="form-group">
            <label htmlFor="familyName" className="form-label">
              {t('familyName')} *
            </label>
            <input
              type="text"
              id="familyName"
              name="familyName"
              value={formData.familyName}
              onChange={handleInputChange}
              placeholder={t('enterFamilyName')}
              className={`form-input ${errors.familyName ? 'error' : ''}`}
              disabled={true}
            />
            {errors.familyName && (
              <span className="error-message">{errors.familyName}</span>
            )}
          </div>

          {/* Phone */}
          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              {t('phone')} *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t('enterPhoneNumber')}
              className={` form-input ${errors.phone ? 'error' : ''}`}
              disabled={true}
            />
            {errors.phone && (
              <span className="error-message">{errors.phone}</span>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('enterEmailAddress')}
              className={`form-input ${errors.email ? 'error' : ''}`}
              disabled={true}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('password')} {t('(optional)')}
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t('enterNewPassword')}
                className={`form-input ${errors.password ? 'error' : ''}`}
                disabled={true}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="password-toggle-btn"
                tabIndex={-1}
                aria-label={showPassword ? t('hidePassword') : t('showPassword')}
              >
                <img 
                  src={showPassword ? "/eye.svg" : "/eye-slash.svg"} 
                  alt={showPassword ? t('hidePassword') : t('showPassword')}
                  className="password-eye-icon"
                />
              </button>
            </div>
            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
          </div>

          {/* Role */}
          <div className="form-group">
            <label htmlFor="role" className="form-label">
              {t('role')} *
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleSelectChange}
              className={`form-select ${errors.role ? 'error' : ''}`}
              disabled={isLoading}
            >
              <option value="viewer">{t('viewer')}</option>
              <option value="manager">{t('manager')}</option>
            </select>
            {errors.role && (
              <span className="error-message">{errors.role}</span>
            )}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="cancel-btn"
              disabled={isLoading}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="add-btn"
              disabled={isLoading}
            >
              {isLoading ? t('updating') : isSuccess ? t('updatedSuccessfully') : t('updateOrganizer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOrganizerForm;